import "./src/styles/global.css";
import * as React from "react";
import { navigate } from "gatsby";

const TRANSITION_MS = 260;
let isTransitioning = false;

const shouldHandleAnchor = (anchor: HTMLAnchorElement, event: MouseEvent) => {
	if (event.defaultPrevented || event.button !== 0) return false;
	if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
	if (anchor.target && anchor.target !== "_self") return false;
	if (anchor.hasAttribute("download")) return false;

	const href = anchor.getAttribute("href");
	if (!href || href.startsWith("#")) return false;
	if (href.startsWith("mailto:") || href.startsWith("tel:")) return false;

	const url = new URL(href, window.location.origin);
	if (url.origin !== window.location.origin) return false;
	if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) {
		return false;
	}

	return true;
};

export const onClientEntry = () => {
	if (typeof window === "undefined") return;

	document.addEventListener(
		"click",
		(event) => {
			const target = event.target as Element | null;
			const anchor = target instanceof Element ? (target.closest("a") as HTMLAnchorElement | null) : null;
			if (!anchor || !shouldHandleAnchor(anchor, event)) return;

			const href = anchor.getAttribute("href");
			if (!href) return;

			event.preventDefault();
			if (isTransitioning) return;

			isTransitioning = true;
			document.body.classList.add("page-transition-leave");

			window.setTimeout(() => {
				isTransitioning = false;
				const url = new URL(href, window.location.origin);
				navigate(url.pathname + url.search + url.hash);
			}, TRANSITION_MS);
		},
		{ capture: true }
	);
};

export const onRouteUpdate = ({ location }: { location: Location }) => {
	if (typeof window === "undefined") return;

	document.body.classList.remove("page-transition-leave");
	document.body.classList.add("page-transition-enter");
	window.requestAnimationFrame(() => {
		document.body.classList.add("page-transition-enter-active");
	});

	window.setTimeout(() => {
		document.body.classList.remove("page-transition-enter");
		document.body.classList.remove("page-transition-enter-active");
	}, TRANSITION_MS);

	if (!location.hash) {
		window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
	}
};

const THEME_CLASSES = ["light-blue", "dark-blue", "light-green", "dark-green", "light-red", "dark-red"] as const;

const ThemeApplier: React.FC<React.PropsWithChildren<{ theme: string }>> = ({ theme, children }) => {
	React.useEffect(() => {
		THEME_CLASSES.forEach((t) => document.body.classList.remove(t));
		if (theme) document.body.classList.add(theme);
	}, [theme]);
	return React.createElement(React.Fragment, null, children);
};

export const wrapPageElement = ({ element, props }: { element: React.ReactNode; props: any }) => {
	const theme = props?.pageContext?.websiteSettings?.siteTheme || "light-blue";
	return React.createElement(
		ThemeApplier,
		{ theme },
		React.createElement("div", { className: "page-shell" }, element)
	);
};